import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';

export type LotusLoaderSize = 'small' | 'medium' | 'large';
export type LotusLoaderVariant = 'bloom' | 'spin' | 'pulse';

export interface LotusLoaderProps {
  /** Size of the loader */
  size?: LotusLoaderSize;
  /** Animation variant */
  variant?: LotusLoaderVariant;
  /** Optional loading text */
  text?: string | null;
  /** Full screen mode */
  fullScreen?: boolean;
}

// Animation keyframes
const bloomAnimation = keyframes`
  0% {
    transform: scale(0.8) rotate(0deg);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: scale(1) rotate(180deg);
    opacity: 1;
  }
`;

const petalFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px) rotate(-10deg);
  }
  100% {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
`;

const gentlePulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const spinAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Size configurations
const sizeConfig = {
  small: {
    width: 24,
    height: 24,
    textVariant: 'caption' as const,
  },
  medium: {
    width: 48,
    height: 48,
    textVariant: 'body1' as const,
  },
  large: {
    width: 96,
    height: 96,
    textVariant: 'h6' as const,
  },
};

/**
 * LotusLoader Component
 * 
 * A culturally-inspired loading animation featuring a lotus bloom motif.
 * Designed for IBOP - Insurance Back Office Platform.
 * 
 * @example
 * <LotusLoader size="large" variant="bloom" text="กำลังโหลดข้อมูล..." />
 */
const LotusLoader: React.FC<LotusLoaderProps> = ({
  size = 'medium',
  variant = 'bloom',
  text = null,
  fullScreen = false,
}) => {
  const config = sizeConfig[size];

  // Lotus SVG component
  const LotusIcon = () => (
    <svg
      width={config.width}
      height={config.height}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="lotusGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1976d2" />
          <stop offset="100%" stopColor="#1565c0" />
        </linearGradient>
      </defs>
      
      {/* Center circle */}
      <circle
        cx="50"
        cy="50"
        r="8"
        fill="url(#lotusGradient)"
        style={{
          animation: `${gentlePulse} 2s ease-in-out infinite`,
        }}
      />
      
      {/* 5 Petals */}
      {[0, 72, 144, 216, 288].map((rotation, index) => (
        <g
          key={index}
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: '50px 50px',
            animation: variant === 'bloom' 
              ? `${petalFadeIn} 800ms ease-out ${index * 100}ms forwards`
              : variant === 'spin'
              ? `${spinAnimation} 2s linear infinite`
              : `${gentlePulse} 1.5s ease-in-out ${index * 150}ms infinite`,
            opacity: variant === 'bloom' ? 0 : 1,
          }}
        >
          <ellipse
            cx="50"
            cy="25"
            rx="7"
            ry="18"
            fill="url(#lotusGradient)"
            style={{
              transformOrigin: '50px 50px',
            }}
          />
        </g>
      ))}
      
      {/* Gold accent ring */}
      <circle
        cx="50"
        cy="50"
        r="14"
        stroke="#FFD700"
        strokeWidth="1.5"
        fill="none"
        opacity="0.6"
        style={{
          animation: `${gentlePulse} 3s ease-in-out infinite`,
        }}
      />
    </svg>
  );

  const loaderContent = (
    <>
      <LotusIcon />
      {text && (
        <Typography
          variant={config.textVariant}
          sx={{
            mt: 2,
            color: 'text.secondary',
            fontFamily: '"Noto Sans Thai", sans-serif',
            fontWeight: 400,
          }}
        >
          {text}
        </Typography>
      )}
    </>
  );

  if (fullScreen) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
        role="status"
        aria-live="polite"
        aria-label={text || 'กำลังโหลด'}
      >
        {loaderContent}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
      }}
      role="status"
      aria-live="polite"
      aria-label={text || 'กำลังโหลด'}
    >
      {loaderContent}
    </Box>
  );
};

export default LotusLoader;
